import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { Org, useOrganizations } from "./Organizations";
import { AddForm, Find, IconButton, matches, SectionHead, ShowMore, useShowMore } from "./Section";
import { ErrorState, PageSkeleton } from "../shell/states";

// Configuration → People: everyone who has signed in or been invited, where they belong, who they
// are, and whether they are disabled.
//
// Every configurator sees everyone (docs/DESIGN.md). What they may do to each person comes with
// the person (`can`), and an action they may not take is shown disabled with the reason. The
// database decides regardless.
//
// - Invite: a "+" beside the title. The person waits as "invited" until their first sign-in.
// - Organization: a pencil beside it turns it into its drop-downs, in place. Changing it ends the
//   old membership and keeps it as history.
// - Who they are: a label (teacher, coach, ...) with a pencil. It grants nothing.
// - Sign-in: a lock beside the status disables them; an open lock enables them again.
// - Roles are not a column here: they are the Access screen's, by person, one click away.
//   Nothing here deletes a person.

export type UserGroupType = "teacher" | "coach" | "instructional_leader"
  | "district_administrator" | "school_administrator" | "staff";

export const USER_GROUP_LABEL: Record<UserGroupType, string> = {
  teacher: "Teacher",
  coach: "Coach",
  instructional_leader: "Instructional leader",
  district_administrator: "District administrator",
  school_administrator: "School administrator",
  staff: "Staff",
};

export interface Person {
  subject_id: string;
  email: string | null;
  name: string | null;
  first_seen: string | null;
  disabled: boolean;
  disabled_by?: string | null;
  invited?: boolean;
  invited_at?: string | null;
  organization_id?: string | null;
  sub_organization_id?: string | null;
  user_group_type?: UserGroupType | null;
  can?: { place: boolean; disable: boolean; label: boolean; why_not: string | null };
}

// What the caller configures: whether they operate, and districts and providers by id.
export interface Powers { platform: boolean; districts: string[]; providers: string[] }

/** The organizations this caller may put people in: the whole ones they configure. Pure. */
export function placeable(orgs: Org[], p: Powers): Org[] {
  return orgs.filter((o) =>
    (o.kind === "district" || o.kind === "content_provider" || o.kind === "platform")
    && (p.platform || p.districts.includes(o.organization_id)
        || p.providers.includes(o.organization_id)));
}

/** An organization's schools or teams. Pure. */
export function subOrganizations(orgs: Org[], orgId: string): Org[] {
  return orgs.filter((o) => o.parent_organization_id === orgId);
}

/** Anyone not in an organization first, so they are placed; otherwise the server's order. Pure. */
export function placedLast<T extends Pick<Person, "organization_id">>(people: T[]): T[] {
  return [...people.filter((p) => !p.organization_id), ...people.filter((p) => p.organization_id)];
}

/** Where a person belongs, in words. Pure. */
export function whereLine(p: Pick<Person, "organization_id" | "sub_organization_id">,
                          byId: Map<string, Org>): string | null {
  if (!p.organization_id) return null;
  const org = byId.get(p.organization_id)?.name ?? "an organization";
  const sub = p.sub_organization_id ? byId.get(p.sub_organization_id)?.name : null;
  return sub ? `${org} · ${sub}` : org;
}

/** A person's standing, in words. Pure. */
export function statusLine(p: Pick<Person, "disabled" | "disabled_by" | "invited"
                                          | "invited_at" | "first_seen">): string {
  if (p.disabled) return p.disabled_by ? `Disabled by ${p.disabled_by}` : "Disabled";
  if (p.invited) return `Invited ${day(p.invited_at ?? null)}`.trim();
  return `Signed in since ${day(p.first_seen)}`.trim();
}

function day(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString(undefined, { dateStyle: "medium" }) : "";
}

const said = (e: unknown) => e instanceof ApiError ? (e.detail ?? e.message) : "That didn't work.";

type Open = { who: string; what: "place" | "label" } | null;

export function People({ onOpenAccess }: { onOpenAccess?: (subjectId: string) => void }) {
  const [people, setPeople] = useState<Person[] | null>(null);
  const [powers, setPowers] = useState<Powers | null>(null);
  const [error, setError] = useState<{ status: number; message: string } | null>(null);
  const [open, setOpen] = useState<Open>(null);
  const [inviting, setInviting] = useState(false);
  const [typed, setTyped] = useState("");
  const rows = useShowMore();
  const [actionError, setActionError] = useState<{ who: string; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const organizations = useOrganizations();

  const load = useCallback(() => {
    api.get<{ people: Person[]; powers: Powers }>("/people")
      .then((d) => { setPeople(d.people ?? []); setPowers(d.powers ?? null); })
      .catch((e: unknown) => setError(e instanceof ApiError
        ? { status: e.status, message: e.detail ?? e.message }
        : { status: 0, message: "could not be read" }));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function act(who: string, call: () => Promise<unknown>): Promise<boolean> {
    setBusy(true);
    setActionError(null);
    try {
      await call();
      setOpen(null);
      load();
      return true;
    } catch (e) {
      setActionError({ who, message: said(e) });
      return false;
    } finally {
      setBusy(false);
    }
  }

  if (error?.status === 403) {
    return (
      <div className="gl">
        <h2>People</h2>
        <p className="gl-lede">This screen is for whoever configures a district, a school or a
          content provider.</p>
      </div>
    );
  }
  if (error) return <ErrorState />;
  if (!people) return <PageSkeleton />;

  const orgs = organizations.data?.organizations ?? [];
  const byId = new Map(orgs.map((o) => [o.organization_id, o]));
  const choices = powers ? placeable(orgs, powers) : [];
  const found = placedLast(people).filter((p) => matches(typed, p.name, p.email, whereLine(p, byId)));

  return (
    <div className="gl pp">
      <SectionHead title="People" level={2} addLabel="Invite someone" adding={inviting}
                   onAdd={() => { setInviting(!inviting); setActionError(null); }}
                   disabledWhy={choices.length ? null
                     : "Inviting someone is for a configurator of their organization."} />
      <p className="gl-lede">Everyone, where they belong, and who they are.</p>
      {inviting && (
        <Invite choices={choices} orgs={orgs} busy={busy}
                error={actionError?.who === "invite" ? actionError.message : null}
                onCancel={() => { setInviting(false); setActionError(null); }}
                onInvite={(body) => act("invite", () => api.post("/people/invitations", body))
                  .then((ok) => { if (ok) setInviting(false); })} />
      )}
      {people.length > 0 && (
        <Find label="Find by name, email or organization" value={typed}
              onChange={(v) => { setTyped(v); rows.reset(); }}
              shown={found.length} total={people.length} />)}
      {people.length === 0 ? (
        <p className="rv-mut">None yet.</p>
      ) : (
        <table className="tbl">
          <thead>
            <tr><th>Person</th><th>Organization</th><th>Who they are</th><th>Status</th>
                <th><span className="sr-only">Access</span></th></tr>
          </thead>
          <tbody>
            {found.slice(0, rows.limit).map((p) => {
              const where = whereLine(p, byId);
              const can = p.can ?? { place: false, disable: false, label: false, why_not: null };
              const isOpen = (what: "place" | "label") =>
                open?.who === p.subject_id && open.what === what;
              const who = p.name || p.email || "this person";
              return [
                <tr key={p.subject_id} className={p.disabled ? "pp-disabled" : undefined}>
                  <td>
                    {p.name || <span className="rv-mut">no name given</span>}
                    <div className="d">{p.email}</div>
                  </td>
                  <td>
                    {isOpen("place") ? (
                      <Place person={p} choices={choices} orgs={orgs} busy={busy}
                             onCancel={() => { setOpen(null); setActionError(null); }}
                             onPlace={(body) => act(p.subject_id, () => api.post(
                               `/people/${p.subject_id}/membership`, body))} />
                    ) : (
                      <div className="cell-edit">
                        <span>{where ?? <span className="rv-mut">Not in an organization yet</span>}</span>
                        <IconButton icon="pencil" size="row" label={`Change ${who}'s organization`}
                                    disabled={busy || !can.place} why={can.why_not}
                                    onClick={() => { setOpen({ who: p.subject_id, what: "place" });
                                                     setActionError(null); }} />
                      </div>
                    )}
                  </td>
                  <td>
                    {isOpen("label") ? (
                      <Label person={p} busy={busy}
                             onCancel={() => { setOpen(null); setActionError(null); }}
                             onSave={(label) => act(p.subject_id, () => api.put(
                               `/people/${p.subject_id}/user-group-type`,
                               { user_group_type: label }))} />
                    ) : (
                      <div className="cell-edit">
                        <span>{p.user_group_type ? USER_GROUP_LABEL[p.user_group_type]
                          : <span className="rv-mut">Not said</span>}</span>
                        <IconButton icon="pencil" size="row" label={`Say who ${who} is`}
                                    disabled={busy || !can.label}
                                    why="Their organization's configurators say who they are."
                                    onClick={() => { setOpen({ who: p.subject_id, what: "label" });
                                                     setActionError(null); }} />
                      </div>
                    )}
                  </td>
                  <td>
                    <div className="cell-edit">
                      <span className="d">{statusLine(p)}</span>
                      <IconButton icon={p.disabled ? "unlock" : "lock"} size="row"
                                  label={p.disabled ? `Enable sign-in for ${who}`
                                                    : `Disable sign-in for ${who}`}
                                  disabled={busy || !can.disable}
                                  why={"Disabling is for an operator or their organization's "
                                       + "configurators."}
                                  onClick={() => act(p.subject_id, () => api.post(
                                    `/people/${p.subject_id}/${p.disabled ? "enable" : "disable"}`,
                                    {}))} />
                    </div>
                  </td>
                  <td>
                    {onOpenAccess && (
                      <button type="button" className="btn-link"
                              onClick={() => onOpenAccess(p.subject_id)}>Roles</button>
                    )}
                  </td>
                </tr>,
                actionError?.who === p.subject_id && (
                  <tr key={`${p.subject_id}:err`}>
                    <td colSpan={5}><p className="inc-error">{actionError.message}</p></td>
                  </tr>
                ),
              ];
            })}
          </tbody>
        </table>
      )}
      <ShowMore shown={Math.min(rows.limit, found.length)} total={found.length} onMore={rows.more} />
    </div>
  );
}

interface PlaceBody { organization_id: string; sub_organization_id: string | null }

function OrgFields({ choices, orgs, org, sub, setOrg, setSub }: {
  choices: Org[]; orgs: Org[]; org: string; sub: string;
  setOrg: (v: string) => void; setSub: (v: string) => void;
}) {
  const subs = org ? subOrganizations(orgs, org) : [];
  const kind = orgs.find((o) => o.organization_id === org)?.kind;
  return (
    <>
      <label>
        Organization
        <select value={org} onChange={(e) => { setOrg(e.target.value); setSub(""); }}>
          <option value="">Choose…</option>
          {choices.map((o) => <option key={o.organization_id} value={o.organization_id}>
            {o.name}</option>)}
        </select>
      </label>
      {subs.length > 0 && (
        <label>
          {kind === "content_provider" ? "Content team" : "School"} (optional)
          <select value={sub} onChange={(e) => setSub(e.target.value)}>
            <option value="">The whole organization</option>
            {subs.map((o) => <option key={o.organization_id} value={o.organization_id}>
              {o.name}</option>)}
          </select>
        </label>
      )}
    </>
  );
}

function Invite({ choices, orgs, busy, error, onInvite, onCancel }: {
  choices: Org[]; orgs: Org[]; busy: boolean; error: string | null;
  onInvite: (body: PlaceBody & { email: string; name: string | null }) => void;
  onCancel: () => void;
}) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [org, setOrg] = useState(choices.length === 1 ? choices[0].organization_id : "");
  const [sub, setSub] = useState("");
  return (
    <AddForm saveLabel="Invite" missing={!email.trim() ? "Enter their email." : !org ? "Choose where they belong." : null} busy={busy} error={error}
             onCancel={onCancel}
             onSave={() => onInvite({ email: email.trim(), name: name.trim() || null,
                                      organization_id: org, sub_organization_id: sub || null })}
             note="They are let in when they first sign in with this address, and belong where you
                   put them. Belonging gives them no access to anything: that is given on Access.">
      <label>
        Email
        <input type="email" value={email} autoFocus onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label>
        Name (optional)
        <input value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <OrgFields choices={choices} orgs={orgs} org={org} sub={sub} setOrg={setOrg} setSub={setSub} />
    </AddForm>
  );
}

function Place({ person, choices, orgs, busy, onPlace, onCancel }: {
  person: Person; choices: Org[]; orgs: Org[]; busy: boolean;
  onPlace: (body: PlaceBody) => void; onCancel: () => void;
}) {
  const [org, setOrg] = useState(person.organization_id ?? "");
  const [sub, setSub] = useState(person.sub_organization_id ?? "");
  const same = org === (person.organization_id ?? "") && sub === (person.sub_organization_id ?? "");
  return (
    <AddForm saveLabel="Save" missing={!org ? "Choose where they belong." : same ? "Nothing has changed." : null} busy={busy} onCancel={onCancel}
             onSave={() => onPlace({ organization_id: org, sub_organization_id: sub || null })}
             note={person.organization_id ? "Where they belonged until now is kept as history."
               : undefined}>
      <OrgFields choices={choices} orgs={orgs} org={org} sub={sub} setOrg={setOrg} setSub={setSub} />
    </AddForm>
  );
}

function Label({ person, busy, onSave, onCancel }: {
  person: Person; busy: boolean; onSave: (label: string) => void; onCancel: () => void;
}) {
  const [label, setLabel] = useState<string>(person.user_group_type ?? "");
  return (
    <AddForm saveLabel="Save" missing={label === (person.user_group_type ?? "") ? "Nothing has changed." : null} busy={busy}
             onCancel={onCancel} onSave={() => onSave(label)}
             note="Who someone is gives them nothing. Roles are given on Access.">
      <label>
        Who they are
        <select value={label} autoFocus onChange={(e) => setLabel(e.target.value)}>
          <option value="">Not said</option>
          {(Object.keys(USER_GROUP_LABEL) as UserGroupType[]).map((k) => (
            <option key={k} value={k}>{USER_GROUP_LABEL[k]}</option>
          ))}
        </select>
      </label>
    </AddForm>
  );
}
